(() => {
  if (window.HermeticaHushGiftOffer) return;

  const offer = Object.freeze({
    id: 'hush-eternity-meru-20260702',
    discountTitle: 'FREE Meru + Eternity with every Hush order',
    hushHandle: 'hush-gummies',
    hushProductId: 8523211997404,
    endsAt: Date.parse('2027-01-01T07:59:00Z'),
    gifts: Object.freeze([
      Object.freeze({ role: 'eternity', variantId: 46457732104412 }),
      Object.freeze({ role: 'meru', variantId: 46458846249180 })
    ])
  });
  const source = 'hsf-gift-offer';
  const sessionKey = 'hsf_gift_offer_session';
  const viewedKey = 'hsf_gift_offer_views';
  const unavailableKey = 'hsf_gift_offer_unavailable';
  const currency = window.Shopify?.currency?.active || 'USD';
  const analyticsEnabled = window.HermeticaHushGiftOfferAnalyticsEnabled !== false;
  let reconciliationTail = Promise.resolve();
  let eventTimer = 0;
  let viewMutationTimer = 0;
  let wasEligible = false;
  const wait = (milliseconds) => new Promise((resolve) => window.setTimeout(resolve, milliseconds));

  const newId = () => {
    try {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
    } catch (_) {}
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  };

  const sessionId = (() => {
    try {
      let value = sessionStorage.getItem(sessionKey);
      if (!value) {
        value = newId();
        sessionStorage.setItem(sessionKey, value);
      }
      return value;
    } catch (_) {
      return 'session-unavailable';
    }
  })();

  const emit = (name, params = {}) => {
    if (!analyticsEnabled) return;
    const eventName = `hsf_${name}`;
    const payload = {
      event_id: newId(),
      session_id: sessionId,
      offer_id: offer.id,
      currency,
      ...params
    };
    try {
      if (!window.dataLayer || typeof window.dataLayer.push !== 'function') window.dataLayer = [];
      window.dataLayer.push({ event: eventName, ...payload });
    } catch (_) {}
    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', eventName, { ...payload, transport_type: 'beacon' });
      }
    } catch (_) {}
  };

  const fetchCart = async () => {
    const response = await fetch('/cart.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    });
    if (!response.ok) throw new Error('cart_fetch_failed');
    return response.json();
  };

  const changeLine = async (key, quantity) => {
    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: key, quantity })
    });
    if (!response.ok) throw new Error('cart_change_failed');
    return response.json();
  };

  const addGifts = async (gifts) => {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        items: gifts.map((gift) => ({
          id: gift.variantId,
          quantity: 1,
          properties: {
            _hsf_gift_offer: offer.id,
            _hsf_gift_role: gift.role
          }
        }))
      })
    });
    if (!response.ok) {
      throw new Error(response.status === 422 ? 'gift_unavailable' : 'gift_add_failed');
    }
    return response.json();
  };

  const properties = (item) => item?.properties || {};
  const isTaggedGift = (item) => properties(item)._hsf_gift_offer === offer.id;
  const giftRole = (item) => properties(item)._hsf_gift_role || '';
  const isOfferActive = () => Number.isFinite(offer.endsAt) && Date.now() < offer.endsAt;
  const cartState = (cart) => ({
    cart_item_count: Number(cart?.item_count || 0),
    cart_total: Number(cart?.total_price || 0) / 100
  });
  const purchaseType = (cart, fallback = '') => {
    if (fallback) return fallback;
    const hushLines = (cart?.items || []).filter((item) => item.handle === offer.hushHandle && !isTaggedGift(item));
    if (!hushLines.length) return 'none';
    return hushLines.some((item) => item.selling_plan_allocation) ? 'subscription' : 'one_time';
  };
  const hasHush = (cart) =>
    (cart?.items || []).some(
      (item) => item.handle === offer.hushHandle && !isTaggedGift(item) && Number(item.quantity || 0) > 0
    );
  const taggedItems = (cart) => (cart?.items || []).filter(isTaggedGift);
  const isCartSurface = () => /^\/cart(?:\/|$)/.test(location.pathname);
  const isHushSurface = () =>
    new RegExp(`^/products/${offer.hushHandle}(?:/|$)`).test(location.pathname);
  const itemsForGift = (cart, gift) =>
    (cart?.items || []).filter(
      (item) => Number(item.variant_id) === gift.variantId && Number(item.quantity || 0) > 0
    );
  const taggedLineIsFree = (item) =>
    Number(item.final_line_price ?? item.line_price ?? 0) === 0 &&
    Number(item.quantity || 0) === 1;
  const includesOfferFreeUnit = (item) => {
    const unitPrice = Number(item?.original_price ?? item?.price ?? 0);
    return (
      unitPrice > 0 &&
      (item?.line_level_discount_allocations || []).some(
        (allocation) =>
          allocation?.discount_application?.title === offer.discountTitle &&
          Number(allocation?.amount || 0) >= unitPrice
      )
    );
  };
  const appliedItemForGift = (cart, gift) =>
    itemsForGift(cart, gift).find(includesOfferFreeUnit);
  const eligibilitySignature = (cart) => {
    const hush = (cart?.items || [])
      .filter((item) => item.handle === offer.hushHandle && !isTaggedGift(item))
      .map((item) => {
        const plan = item.selling_plan_allocation?.selling_plan?.id || '';
        return `${item.key}:${item.quantity}:${plan}`;
      })
      .sort()
      .join('|');
    const gifts = (cart?.items || [])
      .filter((item) => offer.gifts.some((gift) => gift.variantId === Number(item.variant_id)))
      .map((item) => {
        const originalLine = Number(item.original_line_price ?? item.line_price ?? 0);
        const finalLine = Number(item.final_line_price ?? item.line_price ?? 0);
        return [
          item.key,
          item.variant_id,
          item.quantity,
          isTaggedGift(item) ? giftRole(item) : 'customer',
          originalLine,
          finalLine
        ].join(':');
      })
      .sort()
      .join('|');
    const discounts = (cart?.cart_level_discount_applications || [])
      .map((discount) => `${discount.title || ''}:${discount.total_allocated_amount || 0}`)
      .sort()
      .join('|');
    return `${hush}::${gifts}::${discounts}`;
  };
  const readUnavailable = () => {
    try {
      const value = JSON.parse(sessionStorage.getItem(unavailableKey) || 'null');
      return value && typeof value === 'object' ? value : null;
    } catch (_) {
      return null;
    }
  };
  const markUnavailable = (cart) => {
    try {
      sessionStorage.setItem(
        unavailableKey,
        JSON.stringify({
          signature: eligibilitySignature(cart),
          retryAfter: Date.now() + 5 * 60 * 1000
        })
      );
    } catch (_) {}
  };
  const clearUnavailable = () => {
    try {
      sessionStorage.removeItem(unavailableKey);
    } catch (_) {}
  };

  const setStatus = (state) => {
    const messages = {
      checking: 'Checking your Hush gift set…',
      applied: 'Eternity + Meru are included free with this Hush order.',
      unavailable: 'Gift availability could not be confirmed. You will never be charged for an automatic gift.',
      request_error: 'Gift status could not be refreshed. Please try again; you will never be charged for an unconfirmed gift.',
      removed: 'The gift set was removed because Hush is no longer in the cart.'
    };
    document.querySelectorAll('[data-hsf-gift-offer-status]').forEach((node) => {
      node.dataset.state = state;
      if (messages[state]) node.textContent = messages[state];
    });
    document.querySelectorAll('[data-hsf-offer-marketing]').forEach((node) => {
      node.hidden = state === 'unavailable';
    });
  };

  const dispatchCartUpdate = (cart, action) => {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId: offer.id,
          data: {
            source,
            itemCount: Number(cart?.item_count || 0),
            action
          }
        }
      })
    );
    document.dispatchEvent(
      new CustomEvent('hsf:gift-cart-reconciled', {
        bubbles: true,
        detail: { cart, action, offerId: offer.id }
      })
    );
    document.dispatchEvent(
      new CustomEvent('cart:updated', {
        bubbles: true,
        detail: { cart, source, giftOfferHandled: true }
      })
    );
    document.dispatchEvent(
      new CustomEvent('cart:refresh', {
        bubbles: true,
        detail: { cart, source, giftOfferHandled: true }
      })
    );
  };

  const removeTaggedLines = async (cart, reason, context) => {
    let current = cart;
    const lines = taggedItems(current);
    if (!lines.length) return { cart: current, mutated: false };

    for (const line of lines) {
      current = await changeLine(line.key, 0);
    }

    emit('gift_remove', {
      placement: context.placement || 'cart',
      reason,
      removed_count: lines.length,
      purchase_type: purchaseType(cart, context.purchase_type),
      ...cartState(current)
    });
    return { cart: current, mutated: true };
  };

  const reconcileOnce = async (cartInput, context = {}) => {
    let cart = cartInput?.items ? cartInput : await fetchCart();
    let mutated = false;
    const eligible = isOfferActive() && hasHush(cart);
    const placement = context.placement || 'cart_event';
    const mode = purchaseType(cart, context.purchase_type);

    if (!eligible) {
      wasEligible = false;
      clearUnavailable();
      const removed = await removeTaggedLines(
        cart,
        isOfferActive() ? 'eligibility_lost' : 'offer_expired',
        context
      );
      cart = removed.cart;
      mutated = removed.mutated;
      if (mutated) {
        setStatus('removed');
        dispatchCartUpdate(cart, 'removed');
      }
      return { cart, eligible: false, complete: false, mutated };
    }

    const unavailable = readUnavailable();
    if (
      unavailable?.signature === eligibilitySignature(cart) &&
      Number(unavailable.retryAfter || 0) > Date.now()
    ) {
      setStatus('unavailable');
      return { cart, eligible: true, complete: false, mutated: false, error: 'gift_set_backoff' };
    }

    setStatus('checking');
    if (!wasEligible) {
      emit('gift_offer_qualified', {
        placement,
        purchase_type: mode,
        ...cartState(cart)
      });
      wasEligible = true;
    }

    const unexpectedTagged = taggedItems(cart).filter((item) => {
      const matchingGift = offer.gifts.find((gift) => gift.role === giftRole(item));
      return !matchingGift || Number(item.variant_id) !== matchingGift.variantId;
    });
    for (const line of unexpectedTagged) {
      cart = await changeLine(line.key, 0);
      mutated = true;
    }

    for (const gift of offer.gifts) {
      const matchingTagged = taggedItems(cart).filter(
        (item) => giftRole(item) === gift.role && Number(item.variant_id) === gift.variantId
      );
      if (matchingTagged.length > 1) {
        for (const duplicate of matchingTagged.slice(1)) {
          cart = await changeLine(duplicate.key, 0);
          mutated = true;
        }
      }
      const primary = taggedItems(cart).find(
        (item) => giftRole(item) === gift.role && Number(item.variant_id) === gift.variantId
      );
      if (primary && Number(primary.quantity || 0) !== 1) {
        cart = await changeLine(primary.key, 1);
        mutated = true;
      }
    }

    // Preserve shopper-owned exact-SKU lines instead of adding duplicates. Shopify's
    // automatic discount cannot reliably target line properties when identical variants
    // coexist, so an existing line intentionally fulfills the free unit and returns to
    // its normal paid price if Hush is removed; only our tagged lines are auto-removed.
    const missing = offer.gifts.filter(
      (gift) => !itemsForGift(cart, gift).length
    );
    if (missing.length) {
      await addGifts(missing);
      cart = await fetchCart();
      mutated = true;
    }

    const needsDiscountSettlement = (candidateCart) =>
      !offer.gifts.every((gift) => Boolean(appliedItemForGift(candidateCart, gift))) ||
      taggedItems(candidateCart).some((item) => !taggedLineIsFree(item));
    // Automatic discounts can lag the cart mutation on slower Shopify edges.
    // Keep the lines briefly while polling before declaring the set unsafe.
    for (const delay of [150, 350, 750, 1500]) {
      if (!needsDiscountSettlement(cart)) break;
      await wait(delay);
      cart = await fetchCart();
    }

    const paidTaggedCoveredByExistingLine = taggedItems(cart).filter((item) => {
      if (taggedLineIsFree(item)) return false;
      const gift = offer.gifts.find(
        (candidate) => candidate.role === giftRole(item) && candidate.variantId === Number(item.variant_id)
      );
      return Boolean(
        gift &&
        itemsForGift(cart, gift).some(
          (candidate) => candidate !== item && includesOfferFreeUnit(candidate)
        )
      );
    });
    for (const line of paidTaggedCoveredByExistingLine) {
      cart = await changeLine(line.key, 0);
      mutated = true;
    }

    const complete = offer.gifts.every((gift) => Boolean(appliedItemForGift(cart, gift)));
    const unsafeTagged = taggedItems(cart).filter((item) => !taggedLineIsFree(item));
    if (!complete || unsafeTagged.length) {
      const cleaned = await removeTaggedLines(cart, complete ? 'discount_not_applied' : 'incomplete_set', context);
      cart = cleaned.cart;
      mutated = mutated || cleaned.mutated;
      markUnavailable(cart);
      setStatus('unavailable');
      emit('gift_offer_error', {
        placement,
        error_type: complete ? 'discount_not_applied' : 'incomplete_set',
        purchase_type: mode,
        ...cartState(cart)
      });
      if (mutated) dispatchCartUpdate(cart, 'safety_cleanup');
      return { cart, eligible: true, complete: false, mutated, error: 'gift_set_unavailable' };
    }

    setStatus('applied');
    clearUnavailable();
    if (mutated) {
      emit('gift_add_success', {
        placement,
        purchase_type: mode,
        gift_count: offer.gifts.length,
        gift_variant_ids: offer.gifts.map((gift) => String(gift.variantId)).join(','),
        ...cartState(cart)
      });
      dispatchCartUpdate(cart, 'applied');
    }
    return { cart, eligible: true, complete: true, mutated };
  };

  const reconcile = (cartInput, context = {}) => {
    const run = () =>
      reconcileOnce(cartInput, context).catch(async (error) => {
        let cart = cartInput?.items ? cartInput : null;
        let cleanupMutated = false;
        try {
          cart = await fetchCart();
          const complete = offer.gifts.every((gift) => Boolean(appliedItemForGift(cart, gift)));
          const unsafe = taggedItems(cart).some((item) => !taggedLineIsFree(item));
          if (complete && !unsafe) {
            clearUnavailable();
            setStatus('applied');
            dispatchCartUpdate(cart, 'request_error_recovered');
            return { cart, eligible: hasHush(cart), complete: true, mutated: false, recovered: true };
          }
          const cleaned = await removeTaggedLines(cart, 'request_error_cleanup', context);
          cart = cleaned.cart;
          cleanupMutated = cleaned.mutated;
        } catch (_) {}
        const unavailableGift = error?.message === 'gift_unavailable';
        if (unavailableGift && cart) markUnavailable(cart);
        setStatus(unavailableGift ? 'unavailable' : 'request_error');
        emit('gift_offer_error', {
          placement: context.placement || 'cart_event',
          error_type: error?.message || 'request_failed',
          purchase_type: purchaseType(cart, context.purchase_type),
          ...cartState(cart)
        });
        if (cleanupMutated) {
          dispatchCartUpdate(cart, unavailableGift ? 'gift_unavailable_cleanup' : 'request_error_cleanup');
        }
        return {
          cart,
          eligible: Boolean(cart && hasHush(cart)),
          complete: false,
          mutated: cleanupMutated,
          error: unavailableGift ? 'gift_unavailable' : 'request_failed'
        };
      });

    const result = reconciliationTail.then(run, run);
    reconciliationTail = result.then(
      () => undefined,
      () => undefined
    );
    return result;
  };

  const observeOfferViews = () => {
    if (!analyticsEnabled) return;
    const nodes = Array.from(
      document.querySelectorAll('[data-hsf-hush-gift-offer]:not([data-hsf-offer-view-bound])')
    );
    if (!nodes.length) return;
    nodes.forEach((node) => {
      node.dataset.hsfOfferViewBound = 'true';
    });
    let viewed = [];
    try {
      viewed = JSON.parse(sessionStorage.getItem(viewedKey) || '[]');
      if (!Array.isArray(viewed)) viewed = [];
    } catch (_) {
      viewed = [];
    }

    const record = (node) => {
      const placement = node.dataset.hsfOfferPlacement || 'unknown';
      if (viewed.includes(placement)) return;
      viewed.push(placement);
      try {
        sessionStorage.setItem(viewedKey, JSON.stringify(viewed));
      } catch (_) {}
      emit('gift_offer_view', { placement });
    };

    if (!('IntersectionObserver' in window)) {
      nodes.forEach(record);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          record(entry.target);
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.25 }
    );
    nodes.forEach((node) => observer.observe(node));
  };

  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-hsf-gift-offer-link]');
    if (!link) return;
    emit('gift_offer_click', {
      placement: link.closest('[data-hsf-hush-gift-offer]')?.dataset.hsfOfferPlacement || 'unknown'
    });
  });

  ['cart:update', 'cart:updated', 'cart:refresh'].forEach((eventName) => {
    document.addEventListener(eventName, (event) => {
      if (event.detail?.data?.source === source || event.detail?.source === source) return;
      if (event.detail?.data?.didError) return;
      if (event.detail?.giftOfferHandled) {
        window.clearTimeout(eventTimer);
        return;
      }
      const suppliedCart = event.detail?.resource || event.detail?.cart || null;
      const suppliedCartIsRelevant =
        Boolean(suppliedCart?.items) && (hasHush(suppliedCart) || taggedItems(suppliedCart).length > 0);
      const hushProductEvent =
        String(event.detail?.data?.productId || '') === String(offer.hushProductId);
      const explicitOfferEvent = Boolean(event.detail?.giftOfferPlacement);
      if (
        !suppliedCartIsRelevant &&
        !hushProductEvent &&
        !explicitOfferEvent &&
        !isCartSurface() &&
        !isHushSurface()
      ) {
        return;
      }
      window.clearTimeout(eventTimer);
      eventTimer = window.setTimeout(() => {
        reconcile(suppliedCart, {
          placement: event.detail?.giftOfferPlacement || 'cart_event',
          purchase_type: event.detail?.purchaseType || ''
        });
      }, 40);
    });
  });

  window.HermeticaHushGiftOffer = Object.freeze({
    offer,
    reconcile,
    isOfferActive,
    isTaggedGift
  });

  const init = () => {
    observeOfferViews();
    if ('MutationObserver' in window && document.body) {
      const mutationObserver = new MutationObserver((mutations) => {
        const hasOfferNode = mutations.some((mutation) =>
          Array.from(mutation.addedNodes).some(
            (node) =>
              node instanceof Element &&
              (node.matches('[data-hsf-hush-gift-offer]') ||
                node.querySelector('[data-hsf-hush-gift-offer]'))
          )
        );
        if (!hasOfferNode) return;
        window.clearTimeout(viewMutationTimer);
        viewMutationTimer = window.setTimeout(observeOfferViews, 0);
      });
      mutationObserver.observe(document.body, { childList: true, subtree: true });
    }
    if (isCartSurface() || isHushSurface()) {
      reconcile(null, { placement: isCartSurface() ? 'cart_page' : 'hush_page_init' });
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
